import { useCallback, useMemo, useState } from "react";
import {
  closeMonthlyExpenseMonth,
  getExpenseCategoryNames,
  getMonthlyExpenseMonth,
  saveMonthlyExpenseDraft,
} from "../../services/monthlyExpense";
import { ExpenseCategoryName, MonthlyExpenseMonth } from "../../types/monthly-expense.type";
import { useLatestAnswer } from "../DailyExpense/useLatestAnswer";
import {
  CloseDraft,
  branchFiguresOf,
  changedBranchInputsOf,
  closeDraftOf,
} from "./monthlyExpenseDraft";

/** The category list is the same for every month, so it is asked once. */
const CATEGORY_REQUEST = "ALL";

const loadMonth = async (yearMonth: string): Promise<MonthlyExpenseMonth | null> => {
  const { data } = await getMonthlyExpenseMonth(yearMonth);
  return data.data;
};

const loadCategories = async (): Promise<ExpenseCategoryName[]> => {
  const { data } = await getExpenseCategoryNames();
  return data.data?.rows ?? [];
};

/** The draft belongs to the answer it was typed over; a new answer starts it over. */
interface Edit {
  source: MonthlyExpenseMonth;
  draft: CloseDraft;
}

export type CloseWrite = "SAVE" | "CLOSE";

/**
 * S4's data: the month and the category names, what head office typed over them, the live
 * figures, and the two writes. บันทึกร่าง sends only the branches that changed; ส่งและปิดเดือน saves
 * those first, so the close counts what is on screen. A refused write keeps the typing (the
 * interceptor has shown the server's message).
 */
export function useMonthlyExpenseClose(yearMonth: string) {
  const monthAnswer = useLatestAnswer(yearMonth, loadMonth);
  const categoryAnswer = useLatestAnswer(CATEGORY_REQUEST, loadCategories);
  const month = monthAnswer.data;
  const [edit, setEdit] = useState<Edit | null>(null);
  const [pendingWrite, setPendingWrite] = useState<CloseWrite | null>(null);

  const draft = useMemo(() => {
    if (!month) return null;
    return edit?.source === month ? edit.draft : closeDraftOf(month);
  }, [month, edit]);

  const updateDraft = useCallback(
    (change: (current: CloseDraft) => CloseDraft) => {
      if (!month) return;
      setEdit((previous) => ({
        source: month,
        draft: change(previous?.source === month ? previous.draft : closeDraftOf(month)),
      }));
    },
    [month],
  );

  const isLive = month?.status === "DRAFT";
  const rows = useMemo(() => month?.rows ?? [], [month]);
  const figures = useMemo(
    () => (draft ? rows.map((row) => branchFiguresOf(row, draft.branches[row.branch_code], isLive)) : []),
    [rows, draft, isLive],
  );
  const changedInputs = useMemo(
    () => (draft && isLive ? changedBranchInputsOf(rows, draft, isLive) : []),
    [rows, draft, isLive],
  );
  const errorCount = draft ? Object.keys(draft.errors).length : 0;
  const { reload: reloadMonth } = monthAnswer;
  const { reload: reloadCategories } = categoryAnswer;
  const reload = useCallback(() => {
    reloadMonth();
    reloadCategories();
  }, [reloadMonth, reloadCategories]);

  /** Sends the changed branches; true when there was nothing to send or the server took it. */
  const saveChanged = async (): Promise<boolean> => {
    if (changedInputs.length === 0) return true;
    try {
      const { data } = await saveMonthlyExpenseDraft(yearMonth, { branches: changedInputs });
      if (data.data) monthAnswer.replace(data.data);
      return true;
    } catch {
      // The interceptor has shown the server's message; the typing stays for another try.
      return false;
    }
  };

  const save = async (): Promise<boolean> => {
    setPendingWrite("SAVE");
    const isSaved = await saveChanged();
    setPendingWrite(null);
    return isSaved;
  };

  const close = async (): Promise<boolean> => {
    setPendingWrite("CLOSE");
    try {
      if (!(await saveChanged())) return false;
      const { data } = await closeMonthlyExpenseMonth(yearMonth);
      if (data.data) monthAnswer.replace(data.data);
      return true;
    } catch {
      // Refused (closed meanwhile, month not over): read the month again so the screen is true.
      monthAnswer.reload();
      return false;
    } finally {
      setPendingWrite(null);
    }
  };

  return {
    month,
    rows,
    draft,
    figures,
    categories: categoryAnswer.data,
    isLive,
    isEditable: Boolean(month?.actions.can_edit),
    changedCount: changedInputs.length,
    errorCount,
    isLoading: monthAnswer.isLoading || categoryAnswer.isLoading,
    isFailed: monthAnswer.isFailed || categoryAnswer.isFailed,
    pendingWrite,
    reload,
    updateDraft,
    save,
    close,
  };
}
